import { View, SxProp } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import React, { useContext } from 'react';

import { Pagination, PaginationParams } from '@/design-system/components/pagination/Pagination';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { EarnStackRoutes } from '../../earn.navigator';
import { CurrenciesIcon } from '../CurrenciesIcon';
import { ActivityHistoryFilterParams } from './ActivityHistoryFilter';
import { RootStackRoutes } from 'types';
import { CurrenciesContext } from '@/currencies/currency.context';
import { UserEarnActionListResponseDataItem } from '@utx/utx-client';
import { TableView } from '@/design-system/components/table/TableView';
import { TextBody } from '@/design-system/components/typography';
import { Icon } from '@/design-system/components/icons/Icon';
import { formatDate } from '@/utils/dateFormatting';
import { getEarnCategoryTranslationKey } from '../../utils/getEarnCategoryTranslationKey';
import { getSubscriptionTypeTagColours } from '../../utils/getSubscriptionTypeTagColours';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { ActivityActionTag } from './ActivityActionTag';

interface ActivityHistoryTabProps {
  filter: ActivityHistoryFilterParams;
  subscriptions: Array<UserEarnActionListResponseDataItem>;
  paginationParams: PaginationParams;
  loading?: boolean;
}

export const ActivityHistoryTab: React.FC<ActivityHistoryTabProps> = ({ filter, loading, subscriptions, paginationParams }) => {
  const navigation = useNavigation();
  const { t } = useTranslation('earn');
  const { getDecimalByAsset } = useContext(CurrenciesContext);

  const onRowPressed = (item: UserEarnActionListResponseDataItem) => {
    navigation.navigate(RootStackRoutes.Earn, {
      screen: EarnStackRoutes.ActivityHistoryDetails,
      params: { subscription: item }
    });
  };

  return (
    <View sx={styles.container}>
      <TableView<UserEarnActionListResponseDataItem>
        loading={loading && !subscriptions}
        data={subscriptions}
        onRowPressed={onRowPressed}
        rowVerticalAlign="flex-start"
        gap={40}
        columns={[
          {
            title: t('earn-invest::glossary.date'),
            index: 'subscriptionDate',
            width: 160,
            render: (item) => (
              <View sx={{ gap: '$1' }}>
                <TextBody semiBold>{formatDate(item.subscriptionDate)}</TextBody>
                <TextBody secondary>{item.refId}</TextBody>
              </View>
            )
          },
          {
            title: t('earn-invest::glossary.type'),
            index: 'type',
            width: 130,
            render: (item) => {
              const itemColour = getSubscriptionTypeTagColours(item.type);
              return (
                <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$4' }}>
                  <ActivityActionTag action={item.type} />
                </View>
              );
            }
          },
          {
            title: t('earn-invest::glossary.product'),
            index: 'asset',
            minWidth: 230,
            render: (item) => (
              <View sx={{ flexDirection: 'row', alignItems: 'center', overflow: 'hidden' }}>
                <CurrenciesIcon
                  asset={item.asset}
                  title={item.offerName}
                  textSize="textBody"
                  textSemiBold
                  subText={t(getEarnCategoryTranslationKey(item.category))}
                />
              </View>
            )
          },
          {
            title: '',
            index: 'type',
            width: 108,
            render: (item) => {
              const apy = formatAmountByDecimals({ value: item.estimatedApyPercent, decimals: 2, suffix: '%' });
              return (
                <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$2' }}>
                  <Icon name="tagApy" width={33} />
                  <TextBody semiBold>{apy}</TextBody>
                </View>
              );
            }
          },
          {
            title: '',
            index: 'tenor',
            width: 96,
            render: (item) => {
              const { key, options } = getEarnTenorTranslationKey(item.tenor);
              const tenor = t(key, options);
              return (
                <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$2' }}>
                  <Icon name="tagMaturity" />
                  <TextBody semiBold>{tenor}</TextBody>
                </View>
              );
            }
          },
          {
            title: t('earn-invest::glossary.amount'),
            index: 'amount',
            align: 'right',
            minWidth: 90,
            render: (item) => (
              <View sx={{ flex: 1, alignItems: 'flex-end' }}>
                <TextBody semiBold sx={{ textAlign: 'right', color: '$textHighlight' }}>
                  {formatAmountByDecimals({
                    value: item.amount,
                    decimals: getDecimalByAsset(item.asset),
                    suffix: item.asset
                  })}
                </TextBody>
              </View>
            )
          }
        ]}
      />
      <View sx={{ height: '$5' }} />
      <View sx={{ alignItems: 'flex-end' }}>
        <Pagination {...paginationParams} />
      </View>
    </View>
  );
};

enum StyleName {
  container = 'container'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    mt: '$8'
  }
};
